import { useContext, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { SalaryAccrualsDto } from "../../../../dtos/documents/SalaryAccruals/SalaryAccrualsDto";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { NFTextInput } from "../../../Shared/NewForms/NFTextInput";
import { NewDocumentModalViewContext } from "../../../Shared/NewDocumentModalView";
import { DocumentModalViewHeader } from "../../../Shared/DocumentModalViewHeader";
import { NFDateInput } from "../../../Shared/NewForms/NFDateInput";
import { NFTextAreaInput } from "../../../Shared/NewForms/NFTextAreaInput";
import { SalaryAccrualsModel } from "../../../../models/Documents/SalaryAccruals/SalaryAccrualsModel";
import { NFTabs } from "../../../Shared/NewForms/NFTabs";
import { NFTab } from "../../../Shared/NewForms/NFTab";
import { NFTabContainer } from "../../../Shared/NewForms/NFTabContainer";
import handleFormSubmit, { DocumentSaveActionType } from "../../../../helpers/DocumentHelper";
import { AppEntities, AppServices } from "../../../../config/entities";
import { importFromExcel, saveObjectToExcel } from "../../../../helpers/SaveObjectToExcel";
import { NFLookupAccount } from "../../../Shared/NewForms/NFLookupAccount";
import { NFLookupCompany } from "../../../Shared/NewForms/NFLookupCompany";
import { NFLookupDivision } from "../../../Shared/NewForms/NFLookupDivision";
import { NFLookupEmployee } from "../../../Shared/NewForms/NFLookupEmployee";
import { NFSalaryAccrualsTable } from "../../../Shared/NewForms/NFSalaryAccrualsTable";
import { NFIntInput } from "../../../Shared/NewForms/NFIntInput";
import { NFLookupSalaryAccruals } from "../../../Shared/NewForms/NFLookupSalaryAccruals";
import { NFSalaryAccrualsTableList } from "../../../Shared/NewForms/NFSalaryAccrualsTableList";
import SalaryAccrualsDetailSkeleton from "./SalaryAccrualsDetailSkeleton";
import { SalaryAccrualsDetailFooter } from "./SalaryAccrualsDetailFooter";

interface IProps {
  model: SalaryAccrualsDto | null;
  modelUpdatedEvent: (newModel: SalaryAccrualsDto) => void;
}

export const SalaryAccrualsDetail = ({ model, modelUpdatedEvent }: IProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    formState,
    getValues,
    watch,
    control,
    trigger,
    ...methods
  } = useForm<SalaryAccrualsModel>();

  const modalViewContext = useContext(NewDocumentModalViewContext);
  const { postHandler, saveHandler, closeHandler, cancelHandler } = modalViewContext;
  const [tabIndex, setTabIndex] = useState(0);
  const [fileName, setFileName] = useState("Salary Accruals");
  const [responseData, setResponseData] = useState([]);
  const [employeeResponseData, setEmployeeResponseData] = useState([]);

  const watchProducts = watch("salaryAccrualEmployees");

  useEffect(() => {
    console.log("model - ", model);
    register("salaryAccrualEmployees");
    if (model) {
      setValue("id", model.id);
      setValue("documentNumber", model.documentNumber);
      setValue("documentDate", model.documentDate);
      setValue("createdDate", model.createdDate);
      setValue("comment", model.comment);
      setValue("companyId", model.companyId);
      setValue("baseDocumentAtId", model.baseDocumentAtId);
      setValue("divisionId", model.divisionId);
      setValue("month", model.month);
      setValue("withholdingTaxInterimAccountId", model.withholdingTaxInterimAccountId);
      setValue("withholdingTaxInterimAccountId", model.withholdingTaxInterimAccountId);
      setValue("interimAccountOfPensionContributionEmployeeId", model.interimAccountOfPensionContributionEmployeeId);
      setValue("salaryAccrualEmployees", model.salaryAccrualEmployees);
      setValue("salaryAccrualAccruals", model.salaryAccrualAccruals);
      setValue("salaryAccrualDeductions", model.salaryAccrualDeductions);
    }
  }, [model]);

  const handleFormSubmitLocal = async (action: DocumentSaveActionType, data: SalaryAccrualsModel) => {
    await handleFormSubmit(action, data, model, saveHandler, postHandler);
  };

  if (!model) {
    return (
      <>
        <SalaryAccrualsDetailSkeleton />
      </>
    );
  }
  const companyId = watch("companyId");
  const month = watch("month");
  const divisionId = watch("divisionId");

  const handlyApplyChanges = () => {
    const requestData = {
      companyId: companyId,
      month: month,
      divisionId: divisionId
    };

    AppServices.Axios.post("/Payrolls/GetEmployees", requestData)
      .then((response) => {
        console.log("Response:", response.data);
        setResponseData(response.data);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };
  const handlyeEmployeePayrollChanges = () => {
    const requestData = {
      companyId: companyId,
      month: month,
      divisionId: divisionId
    };

    AppServices.Axios.post("/Payrolls/GetEmployeesPayroll", requestData)
      .then((response) => {
        console.log("Response:", response.data);
        setEmployeeResponseData(response.data);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };
  const exportToExcel = () => {
    const data = {
      ...model
    };
    const documentInfo = {
      title: AppEntities.SalaryAccruals.title,
      number: model.documentNumber,
      date: model.documentDate
    };
    saveObjectToExcel(data, documentInfo);
  };

  const handleUploadFile = (file: File) => {
    importFromExcel(file, (data: any) => {
      reset(data);
    });
  };

  return (
    <FormProvider
      getValues={getValues}
      setValue={setValue}
      register={register}
      control={control}
      trigger={trigger}
      reset={reset}
      watch={watch}
      handleSubmit={handleSubmit}
      formState={formState}
      {...methods}
    >
      <NFTabs
        onTabChange={(newIndex) => {
          setTabIndex(newIndex);
        }}
      >
        <NFTab title={"Main"} />
        <NFTab title={"Accruals and Deductions"} />
        <NFTab title={"Additional"} />
      </NFTabs>

      <div className={"modal-container-height"}>
        <DocumentModalViewHeader
          onPost={handleSubmit((data) => handleFormSubmitLocal("post", data))}
          onSave={handleSubmit((data) => handleFormSubmitLocal("save", data))}
          onClose={closeHandler}
          onCancel={cancelHandler}
          handleUploadFile={handleUploadFile}
          exportToExcel={exportToExcel}
          fileName={fileName}
          setFileName={setFileName}
        />

        <NFTabContainer index={0} currentIndex={tabIndex}>
          <Row>
            <Col sm={6}>
              <NFTextInput title={"Document #"} disabled fieldName={"documentNumber"} />
            </Col>
            <Col sm={3}>
              <NFDateInput title={"Document Date"} fieldName={"documentDate"} required={true} />
            </Col>
            <Col sm={3}>
              <NFDateInput title="month" fieldName="month" />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <NFLookupCompany title={"Company"} fieldName={"companyId"} />
            </Col>
            <Col sm={6}>
              <NFLookupDivision title={"Division"} fieldName={"divisionId"} companyId={watch("companyId")} />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <NFLookupAccount title={"Withholding tax interim account"} fieldName={"baseDocumentAtId"} />
            </Col>
            <Col sm={6}>
              <NFLookupAccount title="With Holding Tax Interim Account" fieldName="withholdingTaxInterimAccountId" />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <NFLookupAccount title="Interim Account Of Pension Contribution Company" fieldName="interimAccountOfPensionContributionCompanyId" />
            </Col>
            <Col sm={6}>
              <NFLookupAccount title="Interim Account Of Pension Contribution Employee" fieldName="interimAccountOfPensionContributionEmployeeId" />
            </Col>
          </Row>
        </NFTabContainer>

        <NFTabContainer index={1} currentIndex={tabIndex}>
          <div style={{ display: "flex", gap: "50px" }}>
            <NFSalaryAccrualsTable
              fieldArray={"salaryAccrualEmployees"}
              handlyApplyChanges={handlyApplyChanges}
              handlyeEmployeePayrollChanges={handlyeEmployeePayrollChanges}
              responseData={responseData}
              employeeResponseData={employeeResponseData}
            >
              <NFLookupEmployee fieldName={"employeeId"} title={"Employees"} width={300} />
            </NFSalaryAccrualsTable>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <NFSalaryAccrualsTableList fieldArray={"salaryAccrualAccruals"}>
                  <NFLookupSalaryAccruals title="Accrual" fieldName="salaryAccrualId" width={300} />
                  <NFIntInput title="amountNet" fieldName="amountNet" />
                  <NFIntInput title="amountGross" fieldName="amountGross" />
                  <NFIntInput title="pensionEmployeeAmount" fieldName="pensionEmployeeAmount" />
                  <NFIntInput title="pensionCompanyAmount" fieldName="pensionCompanyAmount" />
                  <NFIntInput title="incomeTax" fieldName="incomeTax" />
                </NFSalaryAccrualsTableList>
              </div>
              <div>
                <NFSalaryAccrualsTableList fieldArray={"salaryAccrualDeductions"}>
                  <NFLookupSalaryAccruals title="Deduction" fieldName="salaryAccrualId" width={300} />
                  <NFIntInput title="amountNet" fieldName="amountNet" />
                  <NFIntInput title="amountGross" fieldName="amountGross" />
                  <NFIntInput title="pensionEmployeeAmount" fieldName="pensionEmployeeAmount" />
                  <NFIntInput title="pensionCompanyAmount" fieldName="pensionCompanyAmount" />
                  <NFIntInput title="incomeTax" fieldName="incomeTax" />
                </NFSalaryAccrualsTableList>
              </div>
            </div>
          </div>
        </NFTabContainer>

        <NFTabContainer index={2} currentIndex={tabIndex}>
          <Row>
            <Col sm={12}>
              <NFTextAreaInput fieldName={"comment"} title={"Comment"} required={false} />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <NFTextInput title={"Created User"} fieldName={"createdUser"} disabled={true} />
            </Col>
            <Col sm={6}>
              <NFDateInput title={"Created Date"} fieldName={"createdDate"} disabled={true} />
            </Col>
          </Row>
        </NFTabContainer>
      </div>
      <SalaryAccrualsDetailFooter/>
    </FormProvider>
  );
};
